import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCustomerComplaints } from 'redux/actions/CustomerComplaints';
import Loader from 'components/loader/Loader';
import CommonTable from 'components/commonTable/CommonTable';
import { dateFormate } from 'utils/utils';
import { debounce } from 'lodash';
import CustomerBreadCrumb from 'pages/customers/CustomerBreadCrumb';
import LocationDropdown from 'components/LocationDropdown/LocationDropdown';
import { filterCustomerComplaintData } from './FilterCustomerComplaintData';
import { Checkbox } from 'components/ui/checkbox';


function CustomerComplaintsTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  const [page, setPage] = useState(params?.page ? parseInt(params?.page) : 0);
  const [rowsPerPage, setRowsPerPage] = useState(params?.pageSize ? parseInt(params?.pageSize) : 10);
  const [status, setStatus] = useState(params?.status ? params?.status : []);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState(params?.search ? params?.search : '');
  const tableData = useSelector((state) => state?.userCustomerComplaints?.userCustomerComplaints?.data);
  const loader = useSelector((state) => state?.userCustomerComplaints?.loading);
  const selectedLocation = useSelector((state) => state?.locationDropdown?.selectedLocation);
  const [sosOnly, setSosOnly] = useState(false);

  let filterData = {
    page: params?.search ? 0 : page,
    dateFrom: dateFrom,
    dateTo: dateTo,
    status: status,
    pageSize: rowsPerPage,
    search: search,
  };

  useEffect(() => {
    dispatch(getCustomerComplaints(filterData));
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSearchParams({ ...params, page: newPage });
    const newFilterData = {
      ...filterData,
      page: newPage,
      complainType: sosOnly ? 'sos' : ''
    };
    dispatch(getCustomerComplaints(newFilterData));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setSearchParams({
      ...params,
      page: 0,
      pageSize: newRowsPerPage,
    });
    const newFilterData = {
      ...filterData,
      page: 0,
      pageSize: newRowsPerPage,
      complainType: sosOnly ? 'sos' : ''
    };
    dispatch(getCustomerComplaints(newFilterData));
  };

  const onFilterChange = (val) => {
    setPage(0);
    setStatus(val);
    filterData = { ...filterData, status: val.toString(), page: 0 };
    setSearchParams({
      ...params,
      status: val.toString(),
      page: 0,
    });
    dispatch(getCustomerComplaints(filterData));
  };

  const onRestFilter = () => {
    setStatus('');
    setSearchParams({ ...params, status: '' });
    filterData = { ...filterData, status: '' };
    dispatch(getCustomerComplaints(filterData));
  };

  const onSelectDateRange = (newValue) => {
    setDateFrom(dateFormate(newValue[0]).format('MM/DD/YYYY'));
    setDateTo(dateFormate(newValue[1]).format('MM/DD/YYYY'));
    filterData = {
      ...filterData,
      dateFrom: dateFormate(newValue[0]).format('MM/DD/YYYY'),
      dateTo: dateFormate(newValue[1]).format('MM/DD/YYYY'),
    };
    dispatch(getCustomerComplaints(filterData));
  };

  const onSearch = debounce((e) => {
    setSearch(e);
    setPage(0);
    filterData = { ...filterData, search: e, page: 0 };
    setSearchParams({ ...params, search: e, page: 0, pageSize: '' });
    dispatch(getCustomerComplaints(filterData));
  }, 1000);

  const onClickComplaint = (val) => {
    navigate(`/complaints/detail`, { state: { id: val?.id, name: val?.complainId, demandProfileId: val?.raisedBy}});
  };

  const handleLocationChange = (selectedLocation) => {
    dispatch(getCustomerComplaints({ ...filterData, location: selectedLocation?.id }));
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between w-full gap-3 mb-3">
        <div className="flex items-center gap-4 sm:gap-4 flex-grow sm:flex-grow-0">
          <div className="w-[140px] sm:w-[200px]">
            <LocationDropdown
              onChange={handleLocationChange}
              placeholder="All City"
              fullWidth
            />
          </div>
          <label className="flex items-center gap-1 sm:gap-2 text-[#000] text-[12px] sm:text-[14px] font-semibold leading-none whitespace-nowrap">
            <Checkbox
              checked={sosOnly}
              onCheckedChange={(checked) => {
                const isChecked = checked === true;
                setSosOnly(isChecked);
                setPage(0); // Reset to first page for new filter
                const newFilterData = {
                  page: 0,
                  dateFrom,
                  dateTo,
                  status,
                  pageSize: rowsPerPage,
                  search,
                  location: selectedLocation?.id,
                  complainType: isChecked ? 'sos' : ''
                };
                dispatch(getCustomerComplaints(newFilterData));
              }}
            />
            SOS only
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 sm:gap-3 flex-grow sm:flex-grow-0">
        <CustomerBreadCrumb
          defaultStatus={status}
          filterdata={filterCustomerComplaintData}
          onFilterChange={onFilterChange}
          onRestFilter={onRestFilter}
          onSelectDateRange={onSelectDateRange}
          onSearch={onSearch}
          searchValue={search}
        />
        </div>
      </div>
      {loader ? (
        <Loader />
      ) : (
        <CommonTable
          data={tableData}
          onClickTableVendor={onClickComplaint}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          tabName={'complaints'}
          isClickable={true}
        />
      )}
    </>
  );
}

export default CustomerComplaintsTable;
